/**
 * ENG-9244 — Framer credential materialisation.
 *
 * WHY THIS FILE EXISTS AT ALL
 *
 * Every other `cli_tool` integration authenticates its binary by having the
 * provisioner materialise `cli_tool.env_key` into `.env.integrations` — `gh`
 * reads `GITHUB_TOKEN`, the Linear CLI reads `LINEAR_API_KEY`. Framer is the
 * first one where that is not enough, because Framer's two halves read
 * credentials from two different places:
 *
 *   - the `framer-api` SDK's `connect()` DOES read `FRAMER_API_KEY` and
 *     `FRAMER_PROJECT_URL` from the environment;
 *   - the `@framer/agent` CLI does NOT read either. It reads only
 *     `<configDir>/framer/projects.json`.
 *
 * Verified against @framer/agent 0.0.44 by running it: with `FRAMER_API_KEY`
 * exported and no config file present, the CLI's own debug output is
 * `credentials: no cached key, starting browser auth flow...`, after which it
 * tries to open a browser and start a localhost relay server. On a headless
 * fleet host that cannot succeed. So publishing `env_key` alone would leave
 * every Framer install with a CLI that is permanently unauthenticated while the
 * integration row reads connected — the exact silently-broken shape that shows
 * up weeks later as a four-hundred-deep consecutive-failure counter.
 *
 * WHY WE WRITE THE FILE INSTEAD OF SHELLING OUT TO `project auth`
 *
 * The vendor's own bootstrap command, `agent project auth <projectUrl> <apiKey>`,
 * does nothing but write this file. It performs NO network validation — it
 * accepted a garbage key (`dummy_key_for_node22_test`) and reported
 * `Project <id> saved`. So writing the file directly is behaviourally identical
 * to running the command, and strictly better on three counts: it works before
 * the CLI is installed, it cannot fall back to the browser flow, and it is a
 * pure function we can unit-test without a subprocess or a network.
 *
 * The cost is that we are now coupled to a pre-1.0 on-disk format. That is a
 * real risk and it is deliberate: `version: 2` implies a v1 existed, and the CLI
 * still carries a `credentials.json` legacy path, so this format HAS churned
 * before. {@link FRAMER_PROJECTS_CONFIG_VERSION} is asserted in the tests so a
 * vendor bump fails loudly here rather than silently degrading to a browser
 * prompt on a headless host.
 */

import {
  chmodSync,
  existsSync,
  mkdirSync,
  readFileSync,
  renameSync,
  rmSync,
  statSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import type { ResolvedIntegration } from '../types/integration.js';

/** The `version` field the CLI's zod schema pins `projects.json` to. */
export const FRAMER_PROJECTS_CONFIG_VERSION = 2;

/**
 * A bare Framer project id: exactly 20 alphanumerics. Mirrors the CLI's own
 * `/^[A-Za-z0-9]{20}$/u`.
 */
const BARE_PROJECT_ID = /^[A-Za-z0-9]{20}$/u;

/**
 * The `<SiteName>--<projectId>` slug form that appears in a project URL path.
 * Mirrors the CLI's `/^.+--([A-Za-z0-9]+)/u`.
 */
const SLUGGED_PROJECT_ID = /^.+--([A-Za-z0-9]+)/u;

/** One project's stored credential, matching the CLI's `ProjectsConfigSchema`. */
export interface FramerProjectCredential {
  apiKey: string;
  userId?: string;
  name?: string;
  lastUsedAt?: string;
}

export interface FramerProjectsConfig {
  version: typeof FRAMER_PROJECTS_CONFIG_VERSION;
  projects: Record<string, FramerProjectCredential>;
}

/**
 * Resolve a Framer project URL (or a bare id) to the project id the CLI keys
 * `projects.json` by.
 *
 * Reimplements the CLI's `parseProjectId` exactly, because the id we write the
 * credential under MUST match the id the CLI looks it up by. If these two ever
 * disagree the CLI finds no cached key and silently falls into the browser auth
 * flow — a failure that presents as a hang, not as an error.
 *
 * Accepts, in the shapes a customer actually pastes:
 *   - `abcdefghij0123456789`                        (bare id)
 *   - `https://framer.com/projects/My-Site--abcdefghij0123456789`
 *   - `framer.com/projects/My-Site--abcdefghij0123456789?branch=main`
 *
 * Returns `null` for anything it cannot resolve — callers MUST treat null as
 * "do not write a credential" rather than inventing a key, so a malformed URL
 * surfaces as a missing credential instead of an unusable one.
 */
export function parseFramerProjectId(projectUrlOrId: string): string | null {
  const input = projectUrlOrId.trim();
  if (!input) return null;
  if (BARE_PROJECT_ID.test(input)) return input;

  try {
    // Relative inputs ("framer.com/projects/...") resolve against the base, so
    // a customer who strips the scheme still works.
    const segments = new URL(input, 'https://framer.com').pathname
      .split('/')
      .filter(Boolean);
    const marker = segments.findIndex((s) => s.toLowerCase() === 'projects');
    if (marker < 0) return null;

    const raw = segments[marker + 1];
    if (raw === undefined) return null;

    const decoded = decodeURIComponent(raw);
    const candidate = decoded.match(SLUGGED_PROJECT_ID)?.[1] ?? decoded;
    return BARE_PROJECT_ID.test(candidate) ? candidate : null;
  } catch {
    return null;
  }
}

/**
 * The three states an existing `projects.json` can be in, kept distinct because
 * they demand different behaviour and collapsing them loses customer credentials.
 *
 * CodeRabbit caught this on PR #4827 and was right. The first version treated an
 * unrecognised `version` as "empty", which then MERGED INTO NOTHING and wrote a
 * v2 file over the top — silently destroying every project credential in a store
 * we simply did not understand. "Lenient" and "merging" are safe apart and
 * destructive together.
 */
type ParsedExistingConfig =
  /** No file, or bytes we cannot parse as JSON at all. Safe to write fresh. */
  | { kind: 'empty' }
  /** A `version: 2` store we understand and can merge into. */
  | { kind: 'ok'; projects: Record<string, FramerProjectCredential> }
  /**
   * Valid JSON, but a schema version we do not know. We can neither merge into
   * it (we do not know its shape) nor replace it (that is the data loss above),
   * so the only safe move is to leave it completely alone.
   */
  | { kind: 'unsupported' };

function parseExistingConfig(existing: string | null | undefined): ParsedExistingConfig {
  if (!existing || !existing.trim()) return { kind: 'empty' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(existing);
  } catch {
    // Corrupt bytes are not a store worth preserving — the CLI's own zod parse
    // would reject them too, so the agent has no working Framer either way.
    // Rewriting is recovery here, not destruction.
    return { kind: 'empty' };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { kind: 'empty' };
  }
  const candidate = parsed as Partial<FramerProjectsConfig>;
  if (candidate.version !== FRAMER_PROJECTS_CONFIG_VERSION) {
    // Could be a future v3, or a v1 we never handled. Either way: hands off.
    return { kind: 'unsupported' };
  }
  const projects = candidate.projects;
  if (typeof projects !== 'object' || projects === null || Array.isArray(projects)) {
    return { kind: 'empty' };
  }
  return { kind: 'ok', projects: { ...projects } };
}

/** One resolved (project, key) pair to materialise. */
export interface FramerCredentialInput {
  /** A project URL or bare project id, as supplied in the integration config. */
  projectUrlOrId: string;
  apiKey: string;
  /** Optional display name, purely for operator legibility in the file. */
  name?: string;
}

export interface MergeFramerProjectsResult {
  /** The serialised file content, or null when nothing should be written. */
  content: string | null;
  /** Project ids written this pass. */
  written: string[];
  /** Inputs whose project URL could not be resolved to an id. */
  skipped: FramerCredentialInput[];
  /** Project ids we previously managed and have now revoked from the store. */
  removed: string[];
  /**
   * True when an existing store used a schema version we do not understand, so
   * we declined to touch it. Callers MUST NOT write when this is set.
   */
  unsupported: boolean;
}

export interface MergeFramerProjectsOptions {
  /**
   * Project ids this provisioner wrote on a previous pass. Any of these that is
   * NOT in `inputs` this time has been disconnected, and its credential is
   * removed from the store.
   *
   * This is the revocation half, and it is the whole reason we track ownership
   * at all. Without it, disconnecting a Framer integration left a working API
   * key on a shared host and the CLI happily kept using it — a credential that
   * outlives its own revocation. Entries we never wrote (a project the agent
   * authorised interactively) are never touched.
   */
  previouslyManagedIds?: readonly string[];
}

/**
 * Merge our credentials into an existing `projects.json`.
 *
 * UPSERT plus scoped revocation, never a blind replace. A customer with three
 * Framer sites has three project ids in one file (the schema is keyed by project
 * id precisely so it can hold many), and the agent may also have authorised a
 * project interactively. Replacing the file would silently revoke the CLI's
 * access to every project this pass did not cover, which would read to the
 * operator as Framer randomly breaking on the sites they were not touching.
 */
export function mergeFramerProjectsConfig(
  existing: string | null | undefined,
  inputs: readonly FramerCredentialInput[],
  options: MergeFramerProjectsOptions = {},
): MergeFramerProjectsResult {
  const parsed = parseExistingConfig(existing);
  const written: string[] = [];
  const skipped: FramerCredentialInput[] = [];
  const removed: string[] = [];

  if (parsed.kind === 'unsupported') {
    return { content: null, written, skipped: [...inputs], removed, unsupported: true };
  }

  const projects: Record<string, FramerProjectCredential> =
    parsed.kind === 'ok' ? { ...parsed.projects } : {};

  for (const input of inputs) {
    const projectId = parseFramerProjectId(input.projectUrlOrId);
    if (!projectId || !input.apiKey) {
      skipped.push(input);
      continue;
    }
    const previous = projects[projectId];
    projects[projectId] = {
      // Preserve vendor-managed bookkeeping (userId, lastUsedAt) the CLI wrote
      // itself — dropping them would make every provisioning pass look to Framer
      // like a brand new client.
      ...previous,
      apiKey: input.apiKey,
      ...(input.name ? { name: input.name } : {}),
    };
    written.push(projectId);
  }

  // Revoke ONLY ids we previously wrote and no longer resolve. Anything the user
  // established outside this provisioner is not ours to delete.
  const stillManaged = new Set(written);
  for (const id of options.previouslyManagedIds ?? []) {
    if (!stillManaged.has(id) && id in projects) {
      delete projects[id];
      removed.push(id);
    }
  }

  if (written.length === 0 && removed.length === 0) {
    return { content: null, written, skipped, removed, unsupported: false };
  }

  // Tab-indented to match what the CLI itself writes, so a file we author and a
  // file `project auth` authors are byte-comparable during debugging.
  const config: FramerProjectsConfig = {
    version: FRAMER_PROJECTS_CONFIG_VERSION,
    projects,
  };
  return {
    content: `${JSON.stringify(config, null, '\t')}\n`,
    written,
    skipped,
    removed,
    unsupported: false,
  };
}

// ---------------------------------------------------------------------------
// On-disk store writer
//
// Mirrors `xurl-config.ts`, which solves the identical problem for the xurl
// CLI ("reads credentials from $HOME/.xurl, no env-var override exists").
// Framer is the second integration of that shape, so it follows the same
// contract deliberately: fail closed on an unreadable/foreign existing file,
// merge rather than replace, atomic rename, 0600.
// ---------------------------------------------------------------------------

const FRAMER_FILE_MODE = 0o600;

/**
 * Sidecar recording which project ids THIS provisioner wrote.
 *
 * We cannot mark ownership inside `projects.json` itself: the CLI parses it with
 * a zod object schema that strips unknown keys, so any marker we added there
 * would be silently erased the first time the agent ran `project auth`. A
 * sidecar is the only place ownership survives.
 *
 * Without it, revocation is impossible — on a later pass we cannot tell the
 * credential we wrote (and must remove when the integration is disconnected)
 * from one the agent authorised interactively (which is not ours to touch).
 */
const MANAGED_SIDECAR_NAME = '.augmented-managed-projects.json';

function sidecarPathFor(projectsPath: string): string {
  return join(dirname(projectsPath), MANAGED_SIDECAR_NAME);
}

function readManagedIds(projectsPath: string): string[] {
  try {
    const raw = readFileSync(sidecarPathFor(projectsPath), 'utf-8');
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    // Absent or unreadable: we simply have no ownership record, so we manage
    // nothing yet. Degrades to the pre-revocation behaviour rather than
    // deleting entries we cannot prove are ours.
    return [];
  }
}

function writeManagedIds(projectsPath: string, ids: readonly string[]): void {
  const target = sidecarPathFor(projectsPath);
  try {
    if (ids.length === 0) {
      try { unlinkSync(target); } catch { /* already absent */ }
      return;
    }
    writeFileSync(target, `${JSON.stringify([...ids].sort(), null, '\t')}\n`, {
      mode: FRAMER_FILE_MODE,
    });
  } catch {
    // Best-effort. A missing sidecar costs us revocation on the NEXT pass, not
    // correctness of the credentials we just wrote — and it self-heals as soon
    // as a pass manages to write it.
  }
}

/**
 * Cross-process advisory lock around the read-modify-write of `projects.json`,
 * via `mkdir` (atomic on POSIX, unlike an existsSync/write pair).
 *
 * CodeRabbit asked for this on PR #4827 and the underlying race is real:
 * `renameSync` makes each write atomic but does nothing about lost updates, so
 * two provisioning passes can both read, both merge, and let the second rename
 * discard the first.
 *
 * Scope honestly stated: this serialises OUR writers against each other. It
 * cannot serialise against the `@framer/agent` CLI, which takes no lock and
 * whose source we do not control — no lock we invent here can make a third-party
 * binary wait. That residual race is bounded by the fact that our writes are
 * idempotent and re-run on every provisioning tick, so a lost update self-heals
 * on the next pass rather than persisting.
 */
const LOCK_STALE_MS = 30_000;
/** Pause between lock-acquisition attempts. Bounds the total wait with LOCK_MAX_ATTEMPTS. */
const LOCK_RETRY_WAIT_MS = 20;
/** 50 x 20ms bounds a contended wait at ~1s before we give up and skip the pass. */
const LOCK_MAX_ATTEMPTS = 50;

/**
 * Result of a guarded critical section. Explicit rather than a bare `T`, because
 * the caller MUST be able to tell "ran under the lock" from "never got it".
 */
type LockOutcome<T> = { acquired: true; value: T } | { acquired: false };

function withStoreLock<T>(projectsPath: string, fn: () => T): LockOutcome<T> {
  const lockDir = `${projectsPath}.lock`;

  // The lock lives beside projects.json, so its parent must exist before we can
  // create it. Without this, a first-ever write on a fresh agent fails
  // mkdirSync(lockDir) with ENOENT, which the retry loop below cannot tell apart
  // from "another writer holds it" — so it would burn every attempt and then
  // correctly-but-uselessly fail closed, and Framer would NEVER provision.
  // Caught by the existing write tests the moment the fail-closed path landed.
  try {
    mkdirSync(dirname(projectsPath), { recursive: true, mode: 0o700 });
  } catch {
    // If we cannot even create the config dir there is nothing to lock and
    // nothing to write; the attempt below will fail closed on its own.
  }

  let held = false;
  for (let attempt = 0; attempt < LOCK_MAX_ATTEMPTS && !held; attempt++) {
    try {
      mkdirSync(lockDir);
      held = true;
    } catch {
      // Break a lock left behind by a process that died mid-write, or we would
      // wedge this agent's Framer credentials permanently on one crash.
      try {
        if (Date.now() - statSync(lockDir).mtimeMs > LOCK_STALE_MS) {
          rmSync(lockDir, { recursive: true, force: true });
          continue;
        }
      } catch {
        // The holder released between our failed mkdir and this stat — retry.
        continue;
      }
      // Brief wait before retrying. `Atomics.wait` blocks this thread without
      // burning CPU, unlike a Date.now() spin (CodeRabbit, PR #4827). Waiting on
      // a value that never changes means it always runs the full timeout, which
      // is exactly the sleep we want. Node permits this on the main thread.
      //
      // Deliberately synchronous: every caller of this module is a synchronous
      // file writer inside the provisioner's write path, and the critical
      // section is a few filesystem calls, so a holder is measured in
      // milliseconds. Making it async would force the whole provisioning chain
      // async for a lock that is almost never contended.
      Atomics.wait(new Int32Array(new SharedArrayBuffer(4)), 0, 0, LOCK_RETRY_WAIT_MS);
    }
  }

  // NEVER run the critical section unlocked (CodeRabbit, PR #4827). The first
  // version fell through to `fn()` whether or not it held the lock, so under
  // real contention the lock silently degraded to no lock at all — and silently
  // is the worst part: a guard that fails open without saying so is
  // indistinguishable from one that worked, which is the whole failure class
  // this module exists to remove.
  //
  // Failing CLOSED is safe here precisely because of the property already relied
  // on elsewhere: these writes are idempotent and re-run on every provisioning
  // tick, so skipping a contended pass costs a few minutes of staleness and
  // self-heals. Writing unlocked could cost a lost credential.
  if (!held) {
    process.stderr.write(
      `[framer-credentials] [lock-unavailable] skipped this pass; another writer holds ${lockDir}\n`,
    );
    return { acquired: false };
  }

  try {
    return { acquired: true, value: fn() };
  } finally {
    try { rmSync(lockDir, { recursive: true, force: true }); } catch { /* best-effort */ }
  }
}


function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Resolve the path to `projects.json`, mirroring the CLI's own `getConfigDir()`:
 * `$XDG_CONFIG_HOME/framer` when set, else `$HOME/.config/framer`.
 *
 * KNOWN LIMITATION, deliberately matching existing platform behaviour: agents on
 * a shared host also share `$HOME`, so this file is per-host, not per-agent. Two
 * agents holding different keys for the SAME Framer project id would overwrite
 * each other. `~/.xurl` has had exactly this property since it shipped, and
 * forcing a per-agent `XDG_CONFIG_HOME` would relocate every other XDG-reading
 * tool on the box (`gh` among them) — a far wider blast radius than the problem
 * warrants. Tracked on ENG-9244 rather than fixed by surprise here.
 */
export function getFramerProjectsPath(): string {
  const xdg = process.env['XDG_CONFIG_HOME'];
  if (xdg) return join(xdg, 'framer', 'projects.json');
  const home = process.env['HOME'] ?? process.env['USERPROFILE'] ?? homedir();
  return join(home, '.config', 'framer', 'projects.json');
}

/**
 * Collect the (project, key) pairs to materialise from a set of resolved
 * integrations. `project_url` comes from the integration's config; the key from
 * its `api_key` credential.
 */
export function buildFramerCredentialInputs(
  integrations: readonly ResolvedIntegration[],
): FramerCredentialInput[] {
  const inputs: FramerCredentialInput[] = [];
  for (const integration of integrations) {
    if (integration.definition_id !== 'framer') continue;
    // A broker-delivered row carries no token by construction (ENG-8344) —
    // writing one from a stale local value is the failure that rule exists to
    // stop, and it applies just as much to a file as to an env var.
    if (integration.credentialDelivery === 'broker') continue;

    const apiKey = asString((integration.credentials ?? {})['api_key']);
    const cfg = integration.config ?? {};
    const projectUrlOrId = asString(cfg['project_url']) ?? asString(cfg['project_id']);
    if (!apiKey || !projectUrlOrId) continue;

    inputs.push({
      projectUrlOrId,
      apiKey,
      ...(asString(cfg['project_name']) ? { name: asString(cfg['project_name']) as string } : {}),
    });
  }
  return inputs;
}

/**
 * Persist Framer project credentials into `projects.json` so the `@framer/agent`
 * CLI is actually authenticated, merging with any projects the agent authorised
 * interactively, and revoking any project we previously wrote that is no longer
 * connected.
 *
 * Safe to call on every provisioning refresh: it is idempotent, and it no-ops
 * when there is nothing of ours to write and nothing of ours to revoke.
 *
 * Returns the absolute path written to, or `null` if nothing was written.
 */
export function writeFramerStoreForIntegrations(
  integrations: readonly ResolvedIntegration[],
  filePath: string = getFramerProjectsPath(),
): string | null {
  const inputs = buildFramerCredentialInputs(integrations);

  const outcome = withStoreLock(filePath, (): string | null => {
    const previouslyManagedIds = readManagedIds(filePath);

    // Nothing to add and nothing of ours on record to revoke. Bail before
    // touching the filesystem so an agent without Framer never grows a config.
    if (inputs.length === 0 && previouslyManagedIds.length === 0) return null;

    let existing: string | null = null;
    if (existsSync(filePath)) {
      try {
        existing = readFileSync(filePath, 'utf-8');
      } catch {
        // Fail closed, as xurl does: an unreadable existing file must not be
        // clobbered, or we could wipe credentials for the customer's other sites.
        return null;
      }
    }

    const { content, written, removed, unsupported } = mergeFramerProjectsConfig(
      existing,
      inputs,
      { previouslyManagedIds },
    );

    // An unrecognised schema version: leave the file exactly as it is. Writing
    // would destroy credentials in a store we do not understand, and that is a
    // worse outcome than Framer being unavailable until someone looks.
    if (unsupported) {
      process.stderr.write(
        '[framer-credentials] [unsupported-store-version] left projects.json untouched\n',
      );
      return null;
    }

    if (!content) return null;

    mkdirSync(dirname(filePath), { recursive: true, mode: 0o700 });

    // Atomic write: stage to a sibling temp file created with the restrictive
    // mode, then rename over the target, so an interrupted process can never
    // leave projects.json half-written — which the CLI's zod parse would reject,
    // dropping it into the headless-hostile browser auth flow.
    const tmpPath = `${filePath}.tmp-${process.pid}-${Date.now()}`;
    writeFileSync(tmpPath, content, { mode: FRAMER_FILE_MODE });
    try {
      renameSync(tmpPath, filePath);
    } catch (err) {
      try { unlinkSync(tmpPath); } catch { /* ignore */ }
      throw err;
    }
    try {
      chmodSync(filePath, FRAMER_FILE_MODE);
    } catch {
      // Best-effort; the temp file already carried the restrictive mode.
    }

    // Record ownership only AFTER the credentials landed, so a crash mid-write
    // never leaves us claiming to manage a key we failed to write.
    writeManagedIds(filePath, written);

    if (removed.length > 0) {
      process.stderr.write(
        `[framer-credentials] [revoked] removed ${removed.length} disconnected project credential(s)\n`,
      );
    }

    return filePath;
  });

  // Could not take the lock: nothing was attempted, so report "not written"
  // rather than inventing a path we never wrote. The next provisioning tick
  // retries.
  return outcome.acquired ? outcome.value : null;
}
